/**
 * Write queue + debounce for referencias.json saves.
 *
 * Two entry points:
 * - saveReferencesQueued(works) — immediate, awaitable, serialized.
 *   For discrete user actions (addWork, deleteWork, etc.)
 * - saveReferencesDebounced(getWorks) — debounced 3s (maxWait 30s), serialized.
 *   For rapid micro-edits (updateWork, fichamento, tag changes).
 *
 * Write queue prevents parallel saves. If a save is in flight, the next waits.
 * Debounce collapses rapid edits into a single save.
 */

import { googleDrive } from './index';
import { DriveConflictError } from './driveFetch';
import { withOfflineGuard } from '../offlineQueue/offlineGuard';
import { logger } from '../../utils/logger';
import { driveErrorToast } from '../../utils/driveErrorToast';
import { bigwaToast } from '../../utils/bigwaToast';
import type { Work } from '../../types/work';

// ---------- Write Queue ----------

let _lock: Promise<void> = Promise.resolve();

function withLock(fn: () => Promise<void>): Promise<void> {
  const prev = _lock;
  let resolve!: () => void;
  let reject!: (error: unknown) => void;
  const result = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
  _lock = prev.then(
    () => fn().then(resolve, reject),
    () => fn().then(resolve, reject)
  );
  _lock.catch(() => {}); // Prevent unhandled rejection on chain
  return result;
}

/** Flag indicando se a última operação foi enfileirada offline */
let _lastOpQueued = false;

/** Retorna true se a última operação saveReferencesQueued foi enfileirada offline */
export function wasLastOpQueued(): boolean { return _lastOpQueued; }

// ---------- Callbacks pós-save (Sprint 030 — delta do índice de marcações) ----------
// Aditivo: observadores são notificados após o save (inclusive enfileirado
// offline — o estado local é a verdade do app); erros de callback nunca
// afetam o save nem o contrato de rollback/conflito.

type AfterSaveCallback = (works: Work[]) => void;
const _afterSaveCallbacks = new Set<AfterSaveCallback>();

/** Registra observador pós-save de referencias.json. Retorna o unsubscribe. */
export function registerAfterReferencesSave(cb: AfterSaveCallback): () => void {
  _afterSaveCallbacks.add(cb);
  return () => { _afterSaveCallbacks.delete(cb); };
}

function notifyAfterSave(works: Work[]): void {
  for (const cb of _afterSaveCallbacks) {
    try {
      cb(works);
    } catch (err) {
      logger.error('referencesWriteQueue', 'After-save callback falhou (ignorado)', err);
    }
  }
}

/**
 * Immediate save through write queue. Awaitable.
 * Clears any pending debounce — discrete action takes priority.
 * Se offline, enfileira no IndexedDB em vez de falhar.
 */
export function saveReferencesQueued(works: Work[]): Promise<void> {
  clearDebounce();
  _latestGetWorks = null;
  _snapshotWorks = null;
  return withLock(async () => {
    const result = await withOfflineGuard(
      'referencias.json',
      JSON.stringify(works),
      () => googleDrive.saveReferencesToDrive(works),
    );
    _lastOpQueued = result.queued;
    notifyAfterSave(works);
  });
}

// ---------- Debounce (3s, maxWait 30s) ----------

const DEBOUNCE_MS = 3_000;
const MAX_WAIT_MS = 30_000;

let _debounceTimer: ReturnType<typeof setTimeout> | null = null;
let _maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
let _latestGetWorks: (() => Work[]) | null = null;
let _snapshotWorks: Work[] | null = null;

/**
 * Debounced save through write queue. Fire-and-forget.
 * getWorks is called when the timer fires to capture latest state.
 */
export function saveReferencesDebounced(getWorks: () => Work[], previousWorks?: Work[]): void {
  _latestGetWorks = getWorks;

  // Capture snapshot only on first call in batch (before edits accumulate)
  if (previousWorks !== undefined && _snapshotWorks === null) {
    _snapshotWorks = previousWorks;
  }

  if (_debounceTimer) clearTimeout(_debounceTimer);

  if (!_maxWaitTimer) {
    _maxWaitTimer = setTimeout(() => flushDebounce(), MAX_WAIT_MS);
  }

  _debounceTimer = setTimeout(() => flushDebounce(), DEBOUNCE_MS);
}

/** Flush pending debounced save immediately. Called on beforeunload. */
export function flushReferencesQueue(): void { flushDebounce(); }

function flushDebounce(): void {
  clearDebounce();
  if (!_latestGetWorks) return;
  const works = _latestGetWorks();
  const rollback = _snapshotWorks;
  _latestGetWorks = null;
  _snapshotWorks = null;
  withLock(async () => {
    const result = await withOfflineGuard(
      'referencias.json',
      JSON.stringify(works),
      () => googleDrive.saveReferencesToDrive(works),
    );
    _lastOpQueued = result.queued;
    notifyAfterSave(works);
    if (result.queued) {
      // Enfileirado offline — manter optimistic update, não fazer rollback
      bigwaToast.info('Salvo localmente. Será sincronizado ao reconectar.', { duration: 3000 });
      return;
    }
  }).catch(err => {
    // Immediate rollback to pre-edit state (before forceReload network call)
    if (rollback) {
      import('../../stores/libraryStore').then(m => {
        m.useLibraryStore.setState({ works: rollback });
      }).catch(() => {});
    }

    if (err instanceof DriveConflictError) {
      bigwaToast.info('Suas referências foram atualizadas em outro dispositivo. Recarregando...', { duration: 5000 });
      import('../../stores/libraryStore').then(m => m.useLibraryStore.getState().forceReload()).catch(() => {});
      return;
    }
    logger.error('referencesWriteQueue', 'Debounced save failed', err);
    driveErrorToast(err, 'Não foi possível salvar as edições. Alterações desfeitas.');
    import('../../stores/libraryStore').then(m => m.useLibraryStore.getState().forceReload()).catch(() => {});
  });
}

function clearDebounce(): void {
  if (_debounceTimer) { clearTimeout(_debounceTimer); _debounceTimer = null; }
  if (_maxWaitTimer) { clearTimeout(_maxWaitTimer); _maxWaitTimer = null; }
}
